import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, ArrowLeft, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const Account = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (!error && data) {
        setProfile(data);
      }
      setLoading(false);
    };

    fetchProfile();
  }, [user]);

  const handleSubscribe = () => {
    navigate('/subscribe');
  };

  const remainingRequests = Math.max(0, 10 - (profile?.trial_requests_used || 0));
  const trialDaysLeft = profile?.trial_start_date
    ? Math.max(
        0,
        7 - Math.floor((Date.now() - new Date(profile.trial_start_date).getTime()) / (1000 * 60 * 60 * 24))
      )
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary/20 to-accent/20 p-4">
      <div className="container mx-auto max-w-4xl py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Translator
        </Button>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
              <CardDescription>Manage your account and subscription</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Username</Label>
                <p className="text-lg">{profile?.username}</p>
              </div>

              <div>
                <Label className="text-sm font-medium">Account Status</Label>
                <div className="mt-2">
                  {profile?.is_subscribed ? (
                    <Badge variant="default">Subscribed</Badge>
                  ) : (
                    <Badge variant="secondary">Trial</Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {!profile?.is_subscribed && (
            <Card>
              <CardHeader>
                <CardTitle>Trial Status</CardTitle>
                <CardDescription>Your current trial usage</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Remaining Requests</span>
                    <span className="text-2xl font-bold text-primary">
                      {remainingRequests} / 10
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3">
                    <div
                      className="bg-primary h-3 rounded-full transition-all"
                      style={{ width: `${((10 - remainingRequests) / 10) * 100}%` }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Trial Days Left</span>
                    <span className="text-2xl font-bold text-primary">
                      {trialDaysLeft} days
                    </span>
                  </div>
                </div>

                {(remainingRequests === 0 || trialDaysLeft === 0) && (
                  <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
                    <p className="text-sm text-destructive font-medium">
                      Your trial has ended. Subscribe to continue using the translator.
                    </p>
                  </div>
                )}

                <Button onClick={handleSubscribe} className="w-full" size="lg">
                  <CreditCard className="mr-2 h-4 w-4" />
                  Subscribe Now - $4.97/month
                </Button>
              </CardContent>
            </Card>
          )}

          {profile?.is_subscribed && profile?.subscription_end_date && (
            <Card>
              <CardHeader>
                <CardTitle>Subscription</CardTitle>
                <CardDescription>Your subscription details</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm">
                  Next billing date:{' '}
                  {formatDistanceToNow(new Date(profile.subscription_end_date), { addSuffix: true })}
                </p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Account Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <Button variant="outline" onClick={signOut} className="w-full">
                Log Out
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const Label = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <span className={className}>{children}</span>
);

export default Account;
